import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Building2, Phone, Mail, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';

const CONDICIONES_IVA = [
  { value: 'responsable_inscripto', label: 'Resp. Inscripto' },
  { value: 'monotributo',           label: 'Monotributo'      },
  { value: 'exento',                label: 'Exento'           },
  { value: 'consumidor_final',      label: 'Consumidor Final' },
  { value: 'no_aplica',             label: 'No aplica'        },
];

const emptyForm = {
  razon_social: '', nombre_fantasia: '', cuit: '', condicion_iva: 'no_aplica',
  telefono: '', email: '', direccion: '', localidad: '', provincia: '', notas: '',
};

function ProveedorForm({ form, onChange }) {
  const set = (f) => (e) => onChange({ ...form, [f]: e.target.value });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label>Razón social *</label>
          <input className="input" value={form.razon_social} onChange={set('razon_social')} placeholder="Ej: Distribuidora SA" />
        </div>
        <div>
          <label>Nombre fantasia</label>
          <input className="input" value={form.nombre_fantasia} onChange={set('nombre_fantasia')} placeholder="Opcional" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label>CUIT</label>
          <input className="input" value={form.cuit} onChange={set('cuit')} placeholder="20-12345678-9" />
        </div>
        <div>
          <label>Condición IVA</label>
          <select className="input" value={form.condicion_iva} onChange={set('condicion_iva')}>
            {CONDICIONES_IVA.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label>Teléfono</label>
          <input className="input" type="tel" value={form.telefono} onChange={set('telefono')} placeholder="11 1234-5678" />
        </div>
        <div>
          <label>Email</label>
          <input className="input" type="email" value={form.email} onChange={set('email')} placeholder="contacto@proveedor.com" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label>Dirección</label>
          <input className="input" value={form.direccion} onChange={set('direccion')} placeholder="Calle y número" />
        </div>
        <div>
          <label>Localidad</label>
          <input className="input" value={form.localidad} onChange={set('localidad')} placeholder="Ciudad" />
        </div>
      </div>

      <div>
        <label>Notas</label>
        <textarea className="input resize-none" rows={2} value={form.notas} onChange={set('notas')} placeholder="Días de entrega, condiciones, etc." />
      </div>
    </div>
  );
}

export default function Proveedores() {
  const { can } = useAuth();
  const [proveedores, setProveedores] = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [search,      setSearch]      = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editItem,  setEditItem]  = useState(null);
  const [form,      setForm]      = useState(emptyForm);
  const [saving,    setSaving]    = useState(false);

  const [confirmId, setConfirmId] = useState(null);
  const [deleting,  setDeleting]  = useState(false);

  const puedeGestionar = can('proveedores.gestionar');

  const fetchProveedores = useCallback(async () => {
    setLoading(true);
    try {
      const params = search ? { search } : {};
      const res = await api.get('/proveedores/index.php', { params });
      setProveedores(res.data);
    } catch { toast.error('Error al cargar proveedores'); }
    finally { setLoading(false); }
  }, [search]);

  useEffect(() => { fetchProveedores(); }, [fetchProveedores]);

  const openCreate = () => { setEditItem(null); setForm(emptyForm); setModalOpen(true); };
  const openEdit   = (p) => {
    setEditItem(p);
    setForm({
      razon_social:   p.razon_social   || '',
      nombre_fantasia:p.nombre_fantasia|| '',
      cuit:           p.cuit           || '',
      condicion_iva:  p.condicion_iva  || 'no_aplica',
      telefono:       p.telefono       || '',
      email:          p.email          || '',
      direccion:      p.direccion      || '',
      localidad:      p.localidad      || '',
      provincia:      p.provincia      || '',
      notas:          p.notas          || '',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.razon_social.trim()) { toast.error('La razón social es requerida'); return; }
    setSaving(true);
    try {
      if (editItem) {
        await api.put(`/proveedores/index.php?id=${editItem.id}`, form);
        toast.success('Proveedor actualizado');
      } else {
        await api.post('/proveedores/index.php', form);
        toast.success('Proveedor creado');
      }
      setModalOpen(false);
      fetchProveedores();
    } catch (err) {
      toast.error(err?.message || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/proveedores/index.php?id=${confirmId}`);
      toast.success('Proveedor eliminado');
      setConfirmId(null);
      fetchProveedores();
    } catch (err) {
      toast.error(err?.message || 'Error al eliminar');
    } finally { setDeleting(false); }
  };

  return (
    <div className="space-y-5">
      <div className="page-header">
        <h1 className="page-title">Proveedores</h1>
        {puedeGestionar && (
          <button className="btn-primary" onClick={openCreate}>
            <Plus size={18} /> Nuevo proveedor
          </button>
        )}
      </div>

      <div className="relative max-w-sm">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          className="input pl-9"
          placeholder="Buscar por nombre, CUIT..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="card !p-0 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
          </div>
        ) : proveedores.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-300">
            <Building2 size={48} strokeWidth={1.5} />
            <p className="text-sm mt-3 text-slate-400">No hay proveedores{search ? ' con ese criterio' : ''}</p>
            {puedeGestionar && !search && (
              <button className="btn-primary mt-4" onClick={openCreate}><Plus size={16} /> Crear el primero</button>
            )}
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>Proveedor</th>
                  <th>CUIT</th>
                  <th>Contacto</th>
                  <th>Localidad</th>
                  <th>Compras</th>
                  {puedeGestionar && <th></th>}
                </tr>
              </thead>
              <tbody>
                {proveedores.map(p => (
                  <tr key={p.id}>
                    <td>
                      <p className="font-medium text-slate-900">{p.razon_social}</p>
                      {p.nombre_fantasia && <p className="text-xs text-slate-400">{p.nombre_fantasia}</p>}
                    </td>
                    <td className="text-slate-500 font-mono text-xs">{p.cuit || <span className="text-slate-300">—</span>}</td>
                    <td>
                      <div className="flex flex-col gap-0.5">
                        {p.telefono && (
                          <a href={`tel:${p.telefono}`} className="flex items-center gap-1 text-xs text-slate-500 hover:text-primary-600 transition-colors">
                            <Phone size={11} /> {p.telefono}
                          </a>
                        )}
                        {p.email && (
                          <a href={`mailto:${p.email}`} className="flex items-center gap-1 text-xs text-slate-500 hover:text-primary-600 transition-colors">
                            <Mail size={11} /> {p.email}
                          </a>
                        )}
                        {!p.telefono && !p.email && <span className="text-slate-300 text-xs">—</span>}
                      </div>
                    </td>
                    <td className="text-slate-500 text-sm">{p.localidad || <span className="text-slate-300">—</span>}</td>
                    <td>
                      <span className="badge-neutral">{p.total_compras} compra/s</span>
                    </td>
                    {puedeGestionar && (
                      <td>
                        <div className="flex items-center gap-1">
                          <button onClick={() => openEdit(p)} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors">
                            <Pencil size={15} />
                          </button>
                          <button onClick={() => setConfirmId(p.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors">
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editItem ? 'Editar proveedor' : 'Nuevo proveedor'}
        footer={
          <>
            <button className="btn-secondary" onClick={() => setModalOpen(false)} disabled={saving}>Cancelar</button>
            <button className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Guardando...' : editItem ? 'Guardar cambios' : 'Crear proveedor'}
            </button>
          </>
        }
      >
        <ProveedorForm form={form} onChange={setForm} />
      </Modal>

      <ConfirmDialog
        isOpen={!!confirmId}
        onClose={() => setConfirmId(null)}
        onConfirm={handleDelete}
        loading={deleting}
        title="Eliminar proveedor"
        message="¿Seguro que querés eliminar este proveedor? Las compras asociadas no se verán afectadas."
      />
    </div>
  );
}
